import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppSettingsService } from '@core';
import { ConfirmationService, PagedList } from '@shared';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { PurchaseInvoice, Supplier, Tax } from '../models';
import { EntityCode } from '@shared/models/entity-code';
import { PurchaseOrder } from 'src/app/purchasing/models/purchasing-purchase-order';
import { PrchLocation } from 'src/app/purchasing/models/prch-location';
import { ServerDrivenTableViewRequest } from '@server-driven-table-view';

@Injectable({
  providedIn: 'root'
})
export class PurchaseInvoiceService {

  baseUrl: string = '';

  /** Emits when a purchase invoice is created/edited/submitted/cancelled/deleted so the listing grid refreshes. */
  readonly purchaseInvoiceFilter$ = new Subject<void>();

  constructor(private httpClient: HttpClient,
    private translateService: TranslateService,
    private confirmationService: ConfirmationService) {
    this.baseUrl = AppSettingsService.appSettings.edaraCoreApi.baseUrl;
    if (this.baseUrl.endsWith('/')) {
      this.baseUrl = this.baseUrl.slice(0, -1);
    }
  }

  //#region Purchase Invoice

  getAllSuppliersAsync(): Promise<Supplier[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<Supplier[]>(this.baseUrl + '/api/Suppliers')
    ).catch(() => { return undefined; });
  }

  listPurchaseInvoicesAsync(query: ServerDrivenTableViewRequest): Observable<PagedList<PurchaseInvoice[]>> {
    return this.httpClient.post<PagedList<PurchaseInvoice[]>>(this.baseUrl + '/api/PurchaseInvoices/List', query);
  }

  getPurchaseInvoiceById(id: string): Promise<PurchaseInvoice | undefined> {
    return firstValueFrom(
      this.httpClient.get<PurchaseInvoice>(`${this.baseUrl}/api/PurchaseInvoices/${id}`)
    ).catch(() => { return undefined; });
  }

  getPurchaseOrdersByDetailsIds(ids: string[]): Promise<PurchaseOrder[] | undefined> {
    return firstValueFrom(
      this.httpClient.post<PurchaseOrder[]>(`${this.baseUrl}/api/PurchaseOrders/GetByDetailsIds`, {
        PurchaseOrdersItemsIds: ids
      })
    ).catch(() => { return undefined; });
  }

  getNextPurchaseInvoiceCode() {
    return this.httpClient.get<EntityCode>(this.baseUrl + '/api/PurchaseInvoices/code');
  }

  getAllLocationsAsync(): Promise<PrchLocation[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<PrchLocation[]>(this.baseUrl + '/api/purchasing/Locations')
    ).catch(() => { return undefined; });
  }

  updatePurchaseInvoice(purchaseInvoice: PurchaseInvoice): Observable<boolean> {
    return this.httpClient.put<boolean>(this.baseUrl + '/api/PurchaseInvoices/' + purchaseInvoice.id, purchaseInvoice.toRequest());
  }

  createPurchaseInvoice(purchaseInvoice: PurchaseInvoice): Observable<PurchaseInvoice> {
    return this.httpClient.post<PurchaseInvoice>(this.baseUrl + '/api/PurchaseInvoices', purchaseInvoice.toRequest());
  }

  submitPurchaseInvoice(purchaseInvoice: PurchaseInvoice) {
    return this.httpClient.put(`${this.baseUrl}/api/PurchaseInvoices/${purchaseInvoice.id}/Submit`, {});
  }

  cancelPurchaseInvoice(purchaseInvoice: PurchaseInvoice, codeString: string) {
    return this.showConfirmation(codeString, 'cancel')
      .pipe(
        filter(Boolean),
        switchMap(() => this.httpClient.put(`${this.baseUrl}/api/PurchaseInvoices/${purchaseInvoice.id}/cancel`, {}))
      );
  }

  deletePurchaseInvoice(purchaseInvoice: PurchaseInvoice, codeString: string) {
    return this.showConfirmation(codeString, 'delete')
      .pipe(
        filter(Boolean),
        switchMap(() => this.httpClient.delete(`${this.baseUrl}/api/PurchaseInvoices/${purchaseInvoice.id}`))
      );
  }

  getAllTaxes(): Observable<Tax[]> {
    return this.httpClient.get<Tax[]>(`${this.baseUrl}/api/Invoicing/taxes`);
  }

  //#endregion

  private showConfirmation(codeString: string, type: 'cancel' | 'delete'): Observable<boolean> {
    return this.confirmationService.confirm(
      this.translateService.instant('invoicing.purchaseInvoice.confirmation.' + type, { code: codeString }),
      this.translateService.instant('invoicing.purchaseInvoice.confirmation.actionCannotBeUnDone'),
      this.translateService.instant('invoicing.purchaseInvoice.confirmation.action.' + type),
      this.translateService.instant('buttons.discard'),
      'warn'
    ).afterClosed()
  }

}
